import { Box, CircularProgress } from "@mui/material";
import Text from "../../../../../components/Text";
import { useGetClaimById } from "../../../../../queries/billings/claims/use-get-claim-by-id";
import { PatientProcedureCode } from "../../../../../services/api";
import EraProcedureSubTable from "../era-eob/EraProcedureSubTable";

interface ClaimEraDetailsTableProps {
  claimUuid: string;
}

/**
 * Procedure-level ERA breakdown for a single claim, rendered inside the claim
 * accordion on the Claims lists. Takes only a claim uuid: the claim payload's
 * `patientProcedureCodes` are the same `PatientProcedureCode[]` the ERA detail
 * page renders, so the ERA sub-table is reused as-is.
 *
 * Read-only — posting stays on the ERA/EOB detail page, which is keyed by ERA
 * uuid rather than claim uuid.
 */
const ClaimEraDetailsTable = ({ claimUuid }: ClaimEraDetailsTableProps) => {
  const { data: claimData, isFetching } = useGetClaimById({ claimUuid });

  const procedures: PatientProcedureCode[] =
    (claimData as any)?.data?.patientProcedureCodes ?? [];

  if (isFetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
        <CircularProgress size={18} />
      </Box>
    );
  }

  if (!procedures.length) {
    return (
      <Box sx={{ py: 1.5, textAlign: "center" }}>
        <Text isTabletext color="text.secondary">
          No CPT details for this claim.
        </Text>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: "auto", py: 1, width: "100%" }}>
      <EraProcedureSubTable procedures={procedures} />
    </Box>
  );
};

export default ClaimEraDetailsTable;
